'use strict';

import Swiper from 'swiper';
import DomUtils from 'helpers/dom-utils';
import Thumbnails from './thumbnails';
import delay from 'helpers/delay';
import debounce from 'debounce';
import Hammer from 'hammerjs';
import { checkTouchSupport } from 'helpers/touch-support'


export class Carousel {

  constructor( options ) {
    if ( typeof options === 'object' && options.carousel_name !== undefined && options.items_data !== undefined && options.carousel_container !== undefined ) {

      // Setup
      this.carousel_container   = options.carousel_container // Required!
      this.items_data           = options.items_data; // Required! - page, json
      this.carousel_name        = options.carousel_name;
      this.pip_type             = options.pip_type        !== undefined ? options.pip_type : 'circles'; // circles, thumbnails
      this.pip_clickable        = options.pip_clickable   !== undefined ? options.pip_clickable : false;
      this.pip_accessible       = options.pip_accessible  !== undefined ? options.pip_accessible : false;
      this.display_pips         = options.display_pips    !== undefined ? options.display_pips : true;
      this.display_arrows       = options.display_arrows  !== undefined ? options.display_arrows : true;
      this.allow_zoom           = options.allow_zoom      !== undefined ? options.allow_zoom : true;
      this.autoplay             = options.autoplay        !== undefined ? options.autoplay : null; // Takes a integer time in ms ie. 2500
      this.scroll_type          = options.scroll_type     !== undefined ? options.scroll_type : 'smooth'; // smooth, fixed, jump
      this.allow_deletion       = options.allow_deletion  !== undefined ? options.allow_deletion : false;
      this.delete_slide_btn     = options.delete_slide_btn !== undefined ? options.delete_slide_btn : null;
      this.delete_slide_handler = options.delete_slide_handler !== undefined ? options.delete_slide_handler : null;
      this.slidesPerView        = options.slidesPerView   !== undefined ? options.slidesPerView : 1;
      this.slidesPerGroup       = options.slidesPerGroup  !== undefined ? options.slidesPerGroup : this.slidesPerView;
      this.loop                 = options.loop            !== undefined ? options.loop : true;
      this.items_select         = options.items_select    !== undefined ? options.items_select : null;
      this.template             = options.template        !== undefined ? options.template : null;
      this.items_json           = options.items_json      !== undefined ? options.items_json : null;
      this.remove_elements      = options.remove_elements !== undefined ? options.remove_elements : null;
      this.effect               = options.effect          !== undefined ? options.effect : 'slide'; // slide, fade
      this.keyboardControl      = options.keyboardControl !== undefined ? options.keyboardControl : false;
      this.enlargeView          = options.enlargeView     !== undefined ? options.enlargeView : false;
      this.nextSlideMessage     = options.nextSlideMessage !== undefined ? options.nextSlideMessage : 'Next Slide';
      this.prevSlideMessage     = options.prevSlideMessage !== undefined ? options.prevSlideMessage : 'Previous Slide';
      this.breakpoints          = options.breakpoints     !== undefined ? options.breakpoints : null;
      this.carouselClass        = this.carousel_name + '-swiper-container';
      this.carouselItems        = [];
      this.thumbnails           = null;
      this.windowWidth          = window.innerWidth;
      this.zoom                 = {
        adjustScale     : 1,
        adjustDeltaX    : 0,
        adjustDeltaY    : 0,
        currentScale    : null,
        currentDeltaX   : null,
        currentDeltaY   : null
      };
      this.carousel;
      this.mc;
      this.tapped = false;

      if(this.carousel_container instanceof HTMLElement){
        this.containerElement = this.carousel_container;
      }else{
        this.containerElement = document.querySelector(this.carousel_container);
      }

      this.mobileThumbnails = this.containerElement.dataset && this.containerElement.dataset.mobileThumbnails;
      this.breakpoint = this.mobileThumbnails ? 0 : 1024;

      // Start Carousel Construction
      if ( this.items_data === 'page' && this.items_select !== undefined ) {
        this.getItemsFromPage( this.items_select );
      } else if ( this.items_data === 'json' ) {
        this.getItemsFromJson(this.items_json)
      }

    } else {
      console.warn( 'Ensure your object is properly constructed and that you have included a carousel_name, carousel_wrapper and items_data property' );
    }
  }

  getItemsFromJson(items) {

    let itemTemplate = this.template;
    let templatedCarouselItems = []

    items.forEach(function(item) {
      let itemHtml = itemTemplate(item);
      let itemMarkup = DomUtils.createElementsFromString(itemHtml)[0];
      templatedCarouselItems.push(itemMarkup);
    });

    this.carouselItems = templatedCarouselItems;
    this.constructCarousel();
  }

  getItemsFromPage( selectorsArray ) {
    for ( let selector of selectorsArray ) {
      let items;
      //selector can either be a query string or a NodeList
      if(selector instanceof NodeList){
        items = selector;
      }else{
        items = this.containerElement.querySelectorAll(selector);
      }
      this.carouselItems = this.carouselItems.concat([...items]); //spread operator converts NodeList to Array
    }

    if ( this.carouselItems.length > 0 ) {

      if ( this.remove_elements && this.remove_elements.length > 0 ) {
        this.removeDomElements();
      }

      this.constructCarousel();

    } else {
      // console.warn( 'Carousel Items have not passed to this.carouselItems array correctly' );
    }
  }

  removeDomElements() {
    for ( let selector of this.remove_elements ) {
      let items;
      //selector can either be a query string or a NodeList
      if(selector instanceof NodeList){
        items = selector;
      }else{
        items = this.containerElement.querySelectorAll(selector);
      }

      for(let item of items){
        item.parentNode.removeChild(item);
      }
    }
  }

  addItemsToCarousel( items, container, type ) {
    for ( let i in items ) {
      if (typeof items[i] === "object") {
        let clonedNode = items[i].cloneNode(true); // Items are cloned to allow for thumbnails images of the same node;
        let item = document.createElement( 'li' );

        item.setAttribute( 'data-carousel', this.carousel_name );
        item.setAttribute( 'data-thumbnail', type === 'thumbnail' ? 'true' : 'false');
        clonedNode.dataset.trackable && type === 'thumbnail' && item.setAttribute('data-media-trackable', "true" );
        item.setAttribute( 'data-carousel-index', i );
        item.classList.add( type === 'thumbnail' ? 'thumbnail-slide' : 'swiper-slide' );
        if(type === 'thumbnail' && clonedNode.nodeName === 'VIDEO'){
          item.classList.add('thumbnail-slide--video');
        }
        item.appendChild(clonedNode);
        container.appendChild(item);
      }
    }
  }

  setVideoDimensions() {
    let firstImage = this.containerElement.querySelector('img');
    let videos = this.containerElement.querySelectorAll('video');
    let videoWidth, videoHeight;
    if (firstImage) {
      videoWidth = firstImage.offsetWidth;
      videoHeight = firstImage.offsetHeight;
      for( let i=0; i<videos.length; i++ ) {
        let video = videos[i];
        video.style.width = `${videoWidth}px`;
        video.style.height = `${videoHeight}px`;
      }
    }
  }

  buildPips( containerElement ) {
    let pips = document.createElement( 'div' ),
      pipsClass = this.carousel_name + '-swiper-pagination';

    pips.classList.add( 'swiper-pagination' );
    pips.classList.add( pipsClass );
    pips.setAttribute( 'aria-hidden', !this.pip_accessible );

    if (this.carousel_name === 'productCarousel') {
      containerElement.parentNode.appendChild( pips );
    } else {
      containerElement.appendChild( pips );
    }
  }

  buildThumbnails( containerElement ) {
    let thumbnailContainer = document.createElement( 'div' ),
      thumbnailInnerWrapper = document.createElement( 'ul' );

    thumbnailContainer.classList.add( 'thumbnail-container' );
    if(this.mobileThumbnails) {
      thumbnailContainer.classList.add( 'thumbnail-container--mobile-thumbnails' );
    }
    if(checkTouchSupport()) {
      thumbnailContainer.classList.add( 'thumbnail-container--touch-slide' );
    }

    thumbnailInnerWrapper.classList.add( 'thumbnail-wrapper' );
    thumbnailContainer.appendChild( thumbnailInnerWrapper );
    containerElement.parentNode.insertBefore( thumbnailContainer, containerElement.nextSibling );
    this.thumbnailsContainer = thumbnailInnerWrapper;

    let thumbnailItems = this.carouselItems;
    this.addItemsToCarousel( thumbnailItems, thumbnailInnerWrapper, 'thumbnail' );
  }

  constructCarousel() {

    let swiperWrapper = document.createElement( 'ul' );

    // Basic Carousel HTML
    this.containerElement.classList.add( this.carouselClass );
    swiperWrapper.classList.add( 'swiper-wrapper' );
    this.containerElement.appendChild( swiperWrapper );

    // Display Pips
    if ( this.display_pips === true && this.carouselItems.length > 1 ) {

      // Set up Pip Circles
      if( this.pip_type === 'circles' ) {
        this.buildPips( this.containerElement );
      }

      // Set up Thumbnails - Initialised in initSwiper Method
      if( this.pip_type === 'thumbnails' && this.windowWidth > this.breakpoint ) {
        this.buildThumbnails( this.containerElement );
      } else {
        this.buildPips( this.containerElement );
      }
    }
    if (this.breakpoints) {
      this.setSlidesPerViewFromBreakPoints();
    }
    // Display Arrows
    if ( this.display_arrows === true && this.carouselItems.length > this.slidesPerView ) {
      let leftArrow = document.createElement( 'button' ),
        rightArrow = document.createElement( 'button' ),
        leftArrowClass = this.carousel_name  + '-swiper-button-prev',
        rightArrowClass = this.carousel_name + '-swiper-button-next';

      leftArrow.classList.add( 'swiper-button-prev' );
      leftArrow.classList.add( leftArrowClass );
      rightArrow.classList.add( 'swiper-button-next' );
      rightArrow.classList.add( rightArrowClass );

      leftArrow.type = 'button'
      rightArrow.type = 'button'
      leftArrow.innerHTML = 'Previous Image';
      rightArrow.innerHTML = 'Next Image';

      // Arrows are inserted before the carousel child
      let carouselChild = this.containerElement.querySelector( '.swiper-wrapper' );
      this.containerElement.insertBefore( leftArrow, carouselChild );
      this.containerElement.insertBefore( rightArrow, carouselChild );
    }

    let itemContainer = this.containerElement.querySelector( '.swiper-wrapper' );
    this.addItemsToCarousel( this.carouselItems, itemContainer );

    this.initSwiper();
  }

  setSlidesPerViewFromBreakPoints() {
    let points = [], point, breakpoint;
    for (point in this.breakpoints) {
      if (this.breakpoints.hasOwnProperty(point)) {
        points.push(point);
      }
    }
    points.sort((a, b) => parseInt(a, 10) > parseInt(b, 10));
    for (var i = 0; i < points.length; i++) {
      point = points[i];
      if (point >= window.innerWidth) {
        breakpoint = point;
        break;
      }
    }
    if (breakpoint) {
      let currentSlidesPerView = this.breakpoints[breakpoint].slidesPerView;
      if (currentSlidesPerView) {
        this.slidesPerView = currentSlidesPerView;
      }
    }
  }

  setVideoHeight() {
    let items = this.containerElement.querySelectorAll('video');

    if(items) {
      this.setVideoDimensions();
      window.addEventListener('resize', debounce(this.setVideoDimensions.bind(this), 200));
    }
  }

  setArias() {
    // Accessibility - Add Aria Hidden to off canvas slides
    let slides = this.containerElement.querySelectorAll( '.swiper-slide' );

    for( let i = 0; i < slides.length; i++ ) {
      slides[i].removeAttribute( 'aria-hidden' );
      if( !slides[i].classList.contains( 'swiper-slide-active' ) ) slides[i].setAttribute( 'aria-hidden', 'true' );
    }

    if( this.slidesPerView > 1 ) {
      let activeSlide = this.containerElement.querySelector( '.swiper-slide-active' );

      for( let i = 0; i < this.slidesPerView - 1; i++ ) {
        let nextSlide = activeSlide.nextSibling;
        if (nextSlide) {
          nextSlide.removeAttribute( 'aria-hidden' );
          activeSlide = nextSlide;
        } else {
          break;
        }
      }
    }
  }

  highlightActiveThumbnail( carousel ) {
    let index = carousel.activeIndex;
    let activeThumbnail = this.thumbnailsContainer.querySelector( 'li[data-carousel-index="' + index +  '"]' );

    // Remove all active thumbnail classes
    const previous = this.thumbnailsContainer.querySelector( '.thumbnail-slide-active' );
    if(previous){
      previous.classList.remove( 'thumbnail-slide-active' );
    }

    activeThumbnail.classList.add( 'thumbnail-slide-active' );
  }

  stopVideos(element) {
    let videos = element.querySelectorAll('video');

    if(videos) {
      for(let i=0; i < videos.length; i++) {
        videos[i].pause();
      }
    }
  }

  onInitSlider() {
    this.setArias();
  }

  onSlideChangeStart( carousel ) {
    if(carousel.imagesToLoad) {
      for (let i = 0; i < carousel.imagesToLoad.length; i++) {
        const image = carousel.imagesToLoad[i];
        if(image.dataset.largeImage && (image.getAttribute("src") !== image.dataset.largeImage)) {
          image.setAttribute("src", image.dataset.largeImage);
        }
      }
    }
    let z = this.zoom;
    let zoomEl = this.containerElement.querySelector( '.swiper-wrapper' );
    let transforms = [];

    if( this.thumbnails ){
      this.highlightActiveThumbnail( carousel );
      this.thumbnails.handleCarouselSlideChange();
    }

    if(z.currentScale > 1) {
      this.zoomOut(zoomEl, z, transforms)
    }

    this.toggleCarouselZoomIcon( carousel.previousIndex, carousel.realIndex ); // Hides and shows the carousel zoom based on whether the slide is a video;

    this.stopVideos( this.containerElement );
  }

  onSlideChangeEnd() {
    this.setArias();
  }

  handleThumbnailClick( event ) {
    event.preventDefault();

    let activeClass = this.containerElement.querySelector( '.thumbnail-slide-active' );

    // Clear all instances of thumbnail active class
    activeClass.classList.remove( 'thumbnail-slide-active' );
  }

  attachThumbnails() {
    return this.pip_type === 'thumbnails' && this.display_pips === true && this.carouselItems.length > this.slidesPerView && this.windowWidth > this.breakpoint;
  }

  buildEnlargeView() {
    // TODO: Make this work for multiple carousels on the page
    let enlargeWrapper = document.createElement( 'div' );
    let attachNode = this.containerElement.parentNode;
    let carousel = this.containerElement;
    let thumbnails = this.containerElement.parentNode.querySelector('.thumbnail-container');


    enlargeWrapper.classList.add( 'enlarge-wrapper' );
    enlargeWrapper.appendChild( carousel );
    enlargeWrapper.appendChild( thumbnails );
    attachNode.appendChild( enlargeWrapper );
  }

  addEnlargeViewListener() {
    let enlargeEl = this.containerElement.querySelector( '.swiper-wrapper' );
    enlargeEl.addEventListener( 'dblclick', this.handleEnlargeView.bind( this ) );
  }

  handleEnlargeView() {
    let enlargeEl = this.containerElement.querySelector('.enlarge-wrapper');
    enlargeEl.classList.toggle( 'in' );
    delay(420, () => {
      this.carousel.update( true );
    });
  }

  handleMobilePinch( event ) {
    let zoomEl = this.containerElement.querySelector( '.swiper-wrapper' );
    let z = this.zoom;
    let transforms = [];
    this.mc.set({touchAction : 'none'});

    if( z.adjustScale * event.scale > 1 ) {
      zoomEl.style.transition = "none";
      if ( z.adjustScale * event.scale < 2.5 ) {
        z.currentScale = z.adjustScale * event.scale;
      }
      this.carousel.disableTouchControl();
      this.hideCarouselControls();
    } else {
      this.mc.set({touchAction : 'auto'});
      zoomEl.style.transition = "0.3s ease-in-out";
      z.currentScale = 1;
      this.carousel.enableTouchControl();
      this.showCarouselControls();
    }

    transforms.push('scale(' + z.currentScale + ')');
    zoomEl.style.transform = transforms;
  }

  handleMobilePan( event ) {
    let zoomEl = this.containerElement.querySelector( '.swiper-wrapper' );
    let z = this.zoom;

    let transforms = [];
    if( z.currentScale > 1 ){
      event.preventDefault();
      zoomEl.style.transition = "none";
      z.currentDeltaX = z.adjustDeltaX + (event.deltaX / z.currentScale);
      z.currentDeltaY = z.adjustDeltaY + (event.deltaY / z.currentScale);
    } else {
      zoomEl.style.transition = "0.3s ease-in-out";
      z.currentDeltaX = 0;
      z.currentDeltaY = 0;
    }

    transforms.push('scale(' + z.currentScale + ') translate(' + z.currentDeltaX + 'px,' + z.currentDeltaY + 'px)');
    zoomEl.style.transform = transforms;
  }

  disableDefaultBrowserBehaviour( event ) {
    if(!event.target.classList.contains('slider-trigger-zoom__icon')) {

      let tappedTimeout;
      if( !this.tapped ) { // Fix for Android phones!
        this.tapped = true;
        tappedTimeout = setTimeout( () => {
          this.tapped = false;
        }, 300);
        event.preventDefault();
      } else {
        clearTimeout( tappedTimeout );
        this.tapped = false;
      }
    }
  }

  toggleZoomButton(option) {
    const zoomButton =  this.containerElement.querySelector('.slider-trigger-zoom' );
    const zoomIcon = this.containerElement.querySelector('.slider-trigger-zoom__icon' );
    zoomButton.disabled = true;
    const label = (option === 'in') ? 'Zoom in product image' : 'Zoom out product image';
    zoomButton.setAttribute('aria-label', label);

    if(option === 'in') {
      zoomIcon.classList.add('slider-trigger-zoom__icon--zoom-in');
      zoomIcon.classList.remove('slider-trigger-zoom__icon--zoom-out');
    }

    if(option === 'out') {
      zoomIcon.classList.add('slider-trigger-zoom__icon--zoom-out');
      zoomIcon.classList.remove('slider-trigger-zoom__icon--zoom-in');
    }
    setTimeout(() => {
      zoomButton.disabled = false;
    }, 500);
  }

  addMobileTouchListeners() {
    let z = this.zoom;

    this.mc = new Hammer.Manager( this.containerElement, {
      touchAction : 'auto'
    });
    this.mc.set({ enable : true })

    // Handle Pinch functionality
    // let pinch = new Hammer.Pinch();
    // this.mc.add( pinch );
    // this.mc.on( 'pinch', this.handleMobilePinch.bind( this ) );
    // this.mc.on( 'pinchend', event => {
    //  z.adjustScale = z.currentScale;
    // })

    // Handle Pan Functionality
    let pan = new Hammer.Pan();
    this.mc.add( pan );
    this.mc.on( 'pan', this.handleMobilePan.bind( this ) );
    this.mc.on( 'panend', () => {
      z.adjustDeltaX = z.currentDeltaX;
      z.adjustDeltaY = z.currentDeltaY;
    })
  }

  applyTransforms(zoomEl, z, transforms) {
    transforms.push(`scale(${z.currentScale})`);
    if( z.currentScale === 1 || z.currentScale === null  ) {
      transforms.push('translate(' + z.currentDeltaX + 'px,' + z.currentDeltaY + 'px)');
    }

    zoomEl.style.transform = transforms.join( ' ' );

    if( z.currentScale !== 1 && z.currentScale !== null ) {
      delay(250, () => {
        zoomEl.style.transition = 'none'
      })
    }
  }

  zoomOut(zoomEl, z, transforms) {
    let scale = 1;

    this.containerElement.removeEventListener( 'touchstart', this.disableDefaultBrowserBehaviour );
    z.currentScale = scale;
    z.adjustScale = scale;
    z.currentDeltaX = 0; // Reset pan position to zero
    z.currentDeltaY = 0; // Reset pan position to zero
    z.adjustDeltaX = 0;
    z.adjustDeltaY = 0;
    zoomEl.style.transition = '0.3s ease-in-out';
    this.carousel.enableTouchControl();
    if(this.windowWidth < this.breakpoint) this.showCarouselControls();
    this.toggleZoomButton('in');
    this.mc.set({touchAction : 'auto'});

    this.applyTransforms(zoomEl, z, transforms)
  }

  zoomIn(zoomEl, z, transforms) {
    let scale = 2.5;
    let currentImage = zoomEl.querySelector('.swiper-slide-active img');

    const largeImage = currentImage.getAttribute('data-large-image');
    if(largeImage) currentImage.src = largeImage
    currentImage.removeAttribute('srcset'); // This allows zoom to pull in full res image!

    this.containerElement.addEventListener( 'touchstart', this.disableDefaultBrowserBehaviour );
    this.mc.set({touchAction : 'none'});

    z.currentScale = scale;
    z.adjustScale = scale;
    this.carousel.disableTouchControl();
    if(this.windowWidth < this.breakpoint) this.hideCarouselControls();
    this.toggleZoomButton('out');

    this.applyTransforms(zoomEl, z, transforms)
  }

  toggleCarouselZoomLevel(event) {

    let target = event.target;
    if(target && (target.classList.contains('swiper-button-prev') || target.classList.contains('swiper-button-next'))) return null;

    if( this.allow_zoom ){
      let zoomEl = this.containerElement.querySelector( '.swiper-wrapper' );
      let z = this.zoom;
      let transforms = [];

      if( z.currentScale === 1 || z.currentScale === null ) {
        this.zoomIn(zoomEl, z, transforms)
      } else {
        this.zoomOut(zoomEl, z, transforms)
      }
    }
  }

  toggleCarouselZoomIcon( previousSlideIndex, currentSlideIndex ) {
    if(!this.allow_zoom) return;

    let slides = this.containerElement.querySelectorAll('li');
    let previousSlide = slides[previousSlideIndex];
    let currentSlide = slides[currentSlideIndex];

    if ((!this.allow_zoom) || (!previousSlide || !currentSlide)) {
      return;
    }

    let previousSlideType = previousSlide.childNodes[0].nodeName;
    let currentSlideType = currentSlide.childNodes[0].nodeName;
    if( currentSlideType !== previousSlideType ) {
      let zoomButton = this.containerElement.querySelector('.slider-trigger-zoom');
      if(currentSlideType === 'VIDEO') {
        zoomButton.style.visibility = 'hidden';
      } else {
        zoomButton.style.visibility = 'visible';
      }
    }
  }

  hideCarouselControls() {
    let prevButton = this.containerElement.querySelector( '.swiper-button-prev' );
    let nextButton = this.containerElement.querySelector( '.swiper-button-next' );
    let pagination = this.containerElement.querySelector( '.swiper-pagination' );

    if( prevButton && pagination ) {
      prevButton.style.left = '-100px';
      nextButton.style.right = '-100px';
      pagination.style.bottom = '-100px';
    }
  }

  showCarouselControls() {
    let prevButton = this.containerElement.querySelector( '.swiper-button-prev' );
    let nextButton = this.containerElement.querySelector( '.swiper-button-next' );
    let pagination = this.containerElement.querySelector( '.swiper-pagination' );

    if( !!prevButton && !!pagination ) {
      delay(250, () => {
        prevButton.style.left = '8px';
        nextButton.style.right = '8px';
        pagination.style.bottom = '8px';
      });
    }
  }

  addZoomIconListener() {
    let zoomButton = this.containerElement.querySelector( '.slider-trigger-zoom' );
    zoomButton.addEventListener( 'click', this.toggleCarouselZoomLevel.bind(this));
  }

  addZoomIcon() {
    let zoomButton = document.createElement( 'button' );
    zoomButton.classList.add( 'slider-trigger-zoom' );
    zoomButton.type = 'button';
    zoomButton.setAttribute('aria-label', 'Zoom in product image');
    zoomButton.innerHTML = '<span class="slider-trigger-zoom__icon slider-trigger-zoom__icon--zoom-in" />';

    this.containerElement.appendChild(zoomButton);
  }

  initSwiper() {
    const pips = this.containerElement.parentNode.querySelector('.swiper-pagination');
    const prevButton = this.containerElement.querySelector( '.swiper-button-prev' );
    const nextButton = this.containerElement.querySelector( '.swiper-button-next' );
    const showPips = this.display_pips && this.carouselItems.length > this.slidesPerView;
    const showArrows = this.display_arrows && this.carouselItems.length > this.slidesPerView;

    const carousel = new Swiper( this.containerElement, {
      pagination          : showPips === true ? pips : null,
      nextButton          : showArrows === true ? nextButton : null,
      prevButton          : showArrows === true ? prevButton : null,
      allowSwipeToPrev    : this.carouselItems.length > this.slidesPerView,
      allowSwipeToNext    : this.carouselItems.length > this.slidesPerView,
      paginationClickable : this.pip_clickable,
      effect              : this.effect,
      loop                : this.carouselItems.length > this.slidesPerView ? this.loop : false,
      keyboardControl     : this.keyboardControl,
      slidesPerView       : this.slidesPerView,
      slidesPerGroup      : this.slidesPerGroup,
      autoplay            : this.autoplay,
      a11y                : true,
      nextSlideMessage    : this.nextSlideMessage,
      prevSlideMessage    : this.prevSlideMessage,
      onSlideChangeEnd    : this.onSlideChangeEnd.bind( this ),
      onInit              : this.onInitSlider.bind( this ),
      onSlideChangeStart  : this.onSlideChangeStart.bind( this ),
      onDoubleTap         : this.allow_zoom ? (swiperInstance, event) => this.toggleCarouselZoomLevel(event) : null, // Used arrow function to fix firefox bug - Change with caution!!
      zoomToggle          : false, // disable Swiper default double tap functionality
      breakpoints         : this.breakpoints
    });

    if( this.attachThumbnails() ) {
      this.thumbnails = new Thumbnails( carousel, {
        thumbnailContainer: this.thumbnailsContainer,
        carouselName      : this.carousel_name,
        spaceBetween      : 16,
        slidesPerView     : 5, // May currently only work accurately with 6 per view TODO - Check/fix this
        count             : this.carouselItems.length
      })
    }

    // handle items deletion
    if (!!this.allow_deletion && this.delete_slide_btn) {
      const deleteButtons = this.containerElement.querySelectorAll(this.delete_slide_btn);
      for (let i = 0; i< deleteButtons.length; i++) {
        let btn = deleteButtons[i];
        btn.addEventListener('click', () => {
          let currentIndex = -1;
          let currentSlide = null;
          for (let i = 0; i < carousel.slides.length; i++) {
            if (btn === carousel.slides[i].querySelector(this.delete_slide_btn)) {
              currentIndex = i;
              currentSlide = carousel.slides[i];
              break;
            }
          }
          if (currentIndex >= 0 && currentSlide) {
            currentSlide.style.transition = 'opacity 400ms'
            currentSlide.style.opacity = 0.1;
            delay(400, () => {
              carousel.removeSlide(currentIndex);
              if (this.delete_slide_handler) {
                this.delete_slide_handler(currentSlide);
              }
            });
          }
        });
      }
    }

    // TODO Add enlarge view for desktop devices
    // if( this.enlargeView && this.attachThumbnails()  ) {
    //  this.carousel = carousel;
    //  this.buildEnlargeView();
    //  this.addEnlargeViewListener();
    // }

    // Ensure that mobile devices allow zoom
    this.carousel = carousel;
    this.addMobileTouchListeners();
    if(this.allow_zoom) {
      this.addZoomIcon();
      this.addZoomIconListener();
    }

    // Ensure that videos are full height of the carousel - TODO Refactor this to not use 3 second timeout
    window.setTimeout(() => {
      this.setVideoHeight();
    }, 3000);
  }
}
